// xlsx-js-style, not plain xlsx - a drop-in fork that adds cell-styling
// support (fill colors etc.), needed for the Diff Net Sal - Bank Paid
// highlight below. Plain xlsx cannot style cells at all.
import * as XLSX from 'xlsx-js-style'
import { PF_ENABLED_GROUP_NAME } from '../config/groupingConfig'
import { toTitleCase } from './textFormat'

function num(value) {
  return Number(value ?? 0)
}

// OT amount is rounded UP (not to nearest), matching the Attendance Report
// and Salary Statement page's OT Total display.
function numCeilOt(value) {
  return Math.ceil(Number(value ?? 0))
}

// Excel-export-only columns (Salary Paid Order / Salary Paid Date, driven
// by the Confirm & Save date) - not shown on screen. confirmed_at is an ISO
// timestamp string (or null/undefined for a row never confirmed); slicing
// the first 10 characters gets its calendar date with no timezone
// conversion involved, so it can't drift a day either way depending on the
// viewer's local time.
function isoDateOnly(isoString) {
  return isoString ? isoString.slice(0, 10) : null
}

function formatDateDMY(isoDateOnlyStr) {
  const [y, m, d] = isoDateOnlyStr.split('-')
  return `${d}/${m}/${y}`
}

// Ranks every distinct confirmation date across ALL sections/groups (not
// just one) chronologically - the earliest date anyone was confirmed on is
// 1, the next distinct date is 2, and so on. Every row confirmed on the
// same date gets the same number, since they were all confirmed together
// in the same Confirm & Save action (or actions on the same day).
function buildConfirmedDateRankMap(sections) {
  const dates = new Set()
  sections.forEach((section) => {
    section.rows.forEach((row) => {
      const d = isoDateOnly(row.confirmed_at)
      if (d) dates.add(d)
    })
  })
  const rankMap = {}
  Array.from(dates).sort().forEach((d, i) => { rankMap[d] = i + 1 })
  return rankMap
}

function computeTotals(rows) {
  return rows.reduce(
    (acc, row) => ({
      grossSalary: acc.grossSalary + num(row.gross_salary),
      paySalary: acc.paySalary + num(row.paySalary),
      otherOt: acc.otherOt + num(row.other_ot),
      otAmount: acc.otAmount + num(row.overtime_amount),
      paySalaryWithOt: acc.paySalaryWithOt + num(row.paySalaryWithOt),
      advance: acc.advance + num(row.advance_deduction),
      netSalary: acc.netSalary + num(row.netSalary),
      basicSalary: acc.basicSalary + num(row.basicSalary),
      pf: acc.pf + num(row.pf),
      esi: acc.esi + num(row.esi),
      totalDeduction: acc.totalDeduction + num(row.totalDeduction),
      totalPayableSalary: acc.totalPayableSalary + num(row.totalPayableSalary),
    }),
    {
      grossSalary: 0, paySalary: 0, otherOt: 0, otAmount: 0, paySalaryWithOt: 0, advance: 0, netSalary: 0,
      basicSalary: 0, pf: 0, esi: 0, totalDeduction: 0, totalPayableSalary: 0,
    }
  )
}

const HEADERS = [
  'Sl No', 'Employee', 'Gross Salary', 'Dept', 'Days in Month', 'Working Days',
  'Leave', 'Total Attendance', 'Pay Salary', 'OT Days', 'OT Hrs', 'OT Other', 'OT Amount',
  'Pay Salary (with OT)', 'Advance', 'Net Salary', 'Paid', 'Confirmed', 'Salary Paid Order',
  'Salary Paid Date', 'Account Name', 'Account Number', 'Final Paid by Bank', 'Diff Net Sal - Bank Paid',
]

// PF group has no OT - Basic/PF/ESI/Total Deduction/Total Payable
// Salary take its place instead (see salaryCalculations.js for the formulas).
// PF sheet only: one blank column inserted right before 'Paid' (so it and
// every column after it shift one letter right, e.g. Paid lands on Q
// instead of P) - a PF-only cosmetic spacer, per explicit request; the
// regular (non-PF) sheet's HEADERS above is untouched.
const PF_HEADERS = [
  'Sl No', 'Employee', 'Gross Salary', 'Dept', 'Days in Month', 'Working Days',
  'Leave', 'Total Attendance', 'Pay Salary', 'Advance', 'Basic (50%)', 'PF (12%)',
  'ESI (0.75%)', 'Total Deduction', 'Total Payable Salary', '', 'Paid', 'Confirmed',
  'Salary Paid Order', 'Salary Paid Date', 'Account Name', 'Account Number', 'Final Paid by Bank',
  'Diff Net Sal - Bank Paid',
]

// Diff Net Sal - Bank Paid always lands in the last column, whether it's
// the regular (24-col) or PF (24-col, with its extra blank spacer earlier)
// header row - both HEADERS and PF_HEADERS are the same length, so this
// index is valid for either sheet variant.
const DIFF_COLUMN_INDEX = HEADERS.length - 1

// Light red fill, applied to a Diff Net Sal - Bank Paid cell whenever the
// live figure has drifted from what was actually locked/paid (either
// direction) - a visual flag for the admin to investigate, not just a
// number buried in the last column.
const DIFF_HIGHLIGHT_STYLE = { fill: { patternType: 'solid', fgColor: { rgb: 'F4CCCC' } } }

function buildGroupBlock(section, month, year, paidMap, confirmedIndexes, dateRankMap) {
  const isPfGroup = section.name === PF_ENABLED_GROUP_NAME
  const totals = computeTotals(section.rows)
  const rows = []
  // Row indices (relative to this block's own `rows` array) whose Diff
  // Net Sal - Bank Paid cell needs the highlight - resolved to absolute
  // sheet coordinates once this block is placed into the full worksheet.
  const highlightRowIndexes = []

  rows.push(['ISKCON BANGALORE HUBLI CENTRE'])
  rows.push([`SALARY STATEMENT FOR THE MONTH OF ${String(month).toUpperCase()} ${year} - ${section.name.toUpperCase()}`])
  rows.push([])
  rows.push(isPfGroup ? PF_HEADERS : HEADERS)

  section.rows.forEach((row, index) => {
    const confirmedDateOnly = isoDateOnly(row.confirmed_at)
    const confirmedNumber = confirmedDateOnly ? dateRankMap[confirmedDateOnly] : ''
    const confirmedDate = confirmedDateOnly ? formatDateDMY(confirmedDateOnly) : ''
    // The final-amount (Net Salary / Total Payable Salary) snapshot frozen
    // at the moment this row was last locked (Confirm & Save Payment) -
    // stays fixed even if today's live figures later change, so it can be
    // compared against what was actually sent to the bank. Blank until
    // ever confirmed; overwritten automatically by a fresh value on every
    // unlock + re-confirm. Last column in the sheet, per explicit request.
    const finalPaidByBank = row.final_paid_by_bank != null ? num(row.final_paid_by_bank) : ''
    // Today's live final amount (Net Salary, or Total Payable Salary for
    // PF) minus what was actually locked/paid - 0 when nothing's changed
    // since the lock, non-zero when a later correction moved the live
    // figure away from what the bank was already sent. Blank until ever
    // confirmed - nothing to compare yet.
    const currentNet = isPfGroup ? num(row.totalPayableSalary) : num(row.netSalary)
    const diffNetSalBankPaid = row.final_paid_by_bank != null ? currentNet - finalPaidByBank : ''
    if (diffNetSalBankPaid !== '' && diffNetSalBankPaid !== 0) {
      highlightRowIndexes.push(rows.length)
    }

    if (isPfGroup) {
      rows.push([
        index + 1,
        row.employee,
        num(row.gross_salary),
        row.department,
        row.daysInMonth ?? 'N/A',
        row.workingDays,
        row.leave,
        row.total_attendance,
        num(row.paySalary),
        num(row.advance_deduction),
        num(row.basicSalary),
        num(row.pf),
        num(row.esi),
        num(row.totalDeduction),
        num(row.totalPayableSalary),
        '',
        paidMap[row.globalIndex] ? 'Yes' : 'No',
        confirmedIndexes.includes(row.globalIndex) ? 'Yes' : 'No',
        confirmedNumber,
        confirmedDate,
        toTitleCase(row.account_name),
        row.account_number,
        finalPaidByBank,
        diffNetSalBankPaid,
      ])
    } else {
      rows.push([
        index + 1,
        row.employee,
        num(row.gross_salary),
        row.department,
        row.daysInMonth ?? 'N/A',
        row.workingDays,
        row.leave,
        row.total_attendance,
        num(row.paySalary),
        row.ot_days ?? 0,
        row.ot_hours ?? 0,
        num(row.other_ot),
        numCeilOt(row.overtime_amount),
        num(row.paySalaryWithOt),
        num(row.advance_deduction),
        num(row.netSalary),
        paidMap[row.globalIndex] ? 'Yes' : 'No',
        confirmedIndexes.includes(row.globalIndex) ? 'Yes' : 'No',
        confirmedNumber,
        confirmedDate,
        toTitleCase(row.account_name),
        row.account_number,
        finalPaidByBank,
        diffNetSalBankPaid,
      ])
    }
  })

  if (isPfGroup) {
    rows.push([
      '', 'TOTAL', totals.grossSalary, '', '', '', '', '',
      totals.paySalary, totals.advance, totals.basicSalary, totals.pf, totals.esi,
      totals.totalDeduction, totals.totalPayableSalary, '', '', '', '', '', '', '', '', '',
    ])
  } else {
    rows.push([
      '', 'TOTAL', totals.grossSalary, '', '', '', '', '',
      totals.paySalary, '', '', totals.otherOt, numCeilOt(totals.otAmount), totals.paySalaryWithOt,
      totals.advance, totals.netSalary, '', '', '', '', '', '', '', '',
    ])
  }

  return { rows, highlightRowIndexes }
}

// Builds a single workbook with a single sheet - every department group
// (Driver, Labor, Staff... plus PF) stacked one below the other, each
// with its own repeated heading/subheading and totals row, matching the
// on-screen/printed Salary Statement's layout exactly rather than splitting
// groups into tabs.
function buildWorkbook(sections, month, year, paidMap, confirmedIndexes) {
  const allRows = []
  const highlightCells = [] // absolute {r, c} coordinates to fill light red
  const dateRankMap = buildConfirmedDateRankMap(sections)

  sections.forEach((section, index) => {
    if (index > 0) {
      allRows.push([])
      allRows.push([])
    }
    const { rows, highlightRowIndexes } = buildGroupBlock(section, month, year, paidMap, confirmedIndexes, dateRankMap)
    const blockStartRow = allRows.length
    highlightRowIndexes.forEach((relativeRow) => {
      highlightCells.push({ r: blockStartRow + relativeRow, c: DIFF_COLUMN_INDEX })
    })
    allRows.push(...rows)
  })

  const worksheet = XLSX.utils.aoa_to_sheet(allRows)
  const columnCount = Math.max(HEADERS.length, PF_HEADERS.length)
  worksheet['!cols'] = Array.from({ length: columnCount }, () => ({ wch: 16 }))

  highlightCells.forEach(({ r, c }) => {
    const cellRef = XLSX.utils.encode_cell({ r, c })
    const cell = worksheet[cellRef]
    if (cell) cell.s = DIFF_HIGHLIGHT_STYLE
  })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Salary Statement')

  return workbook
}

export function exportSalaryStatementToExcel(sections, month, year, paidMap, confirmedIndexes) {
  const workbook = buildWorkbook(sections, month, year, paidMap, confirmedIndexes)
  XLSX.writeFile(workbook, `Salary_Statement_${month}_${year}.xlsx`)
}

// Same workbook as above, returned as a Blob instead of triggering a
// download directly - used by the "Download All" zip (see SalaryStatement.js),
// which needs the raw bytes to bundle alongside the Attendance export.
export function buildSalaryStatementExcelBlob(sections, month, year, paidMap, confirmedIndexes) {
  const workbook = buildWorkbook(sections, month, year, paidMap, confirmedIndexes)
  const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  return new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}
